import { CheckCircle2, RotateCcw } from 'lucide-react';
import { ordenarAchados, type Achado, type Severidade } from '@social/shared';
import type { ResumoAchados } from '../lib/auditoria';

const ROTULO: Record<Severidade, string> = {
  IMPEDITIVO: 'Impede',
  EXIGENCIA: 'Exigência',
  AVISO: 'Aviso',
};

const EXPLICACAO: Record<Severidade, string> = {
  IMPEDITIVO: 'O contrato não é gerado enquanto isto existir.',
  EXIGENCIA: 'Sai, mas a Junta provavelmente devolve.',
  AVISO: 'Não impede nada. É sinal de dado copiado.',
};

/** Cor do selo e da faixa à esquerda, por severidade — tokens `auth-*` do site
 * (ver `estilos.css`), mesmo espírito de `apps/web/src/componentes/painel-laudo.tsx`
 * adaptado à paleta fixa e escura da landing. */
const COR: Record<Severidade, string> = {
  IMPEDITIVO: 'border-auth-erro text-auth-erro bg-auth-erro/10',
  EXIGENCIA: 'border-auth-alerta text-auth-alerta bg-auth-alerta/10',
  AVISO:
    'border-auth-texto-terciario text-auth-texto-terciario bg-auth-superficie/[0.04]',
};

interface Props {
  resultado: { achados: readonly Achado[]; resumo: ResumoAchados };
  onNovaAuditoria: () => void;
}

/**
 * Laudo da auditoria pública anônima — mesma linguagem visual de
 * `painel-laudo.tsx` (selo por severidade, faixa à esquerda, mensagem +
 * fundamento), portada para os tokens da landing.
 *
 * `achado.trecho` nunca é lido aqui: a API não devolve trecho do documento
 * pela rede (ver `AuditoriaService.auditar`), então o campo já chega `null` —
 * mas mesmo que chegasse preenchido, esta tela não é o lugar de mostrá-lo.
 */
export function LaudoPublico({ resultado, onNovaAuditoria }: Props): JSX.Element {
  const ordenados = ordenarAchados(resultado.achados);
  const { impeditivos, exigencias, avisos, total } = resultado.resumo;

  return (
    <div
      aria-live="polite"
      className="cartao-vidro shadow-auth-profundidade flex flex-col gap-5 p-6"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-auth-texto text-lg font-medium tracking-tight">
          Resultado da auditoria
        </h3>
        <button
          type="button"
          onClick={onNovaAuditoria}
          className="text-auth-texto-secundario hover:text-auth-texto inline-flex items-center gap-1.5 text-sm font-medium transition-colors"
        >
          <RotateCcw size={14} aria-hidden />
          Auditar outro contrato
        </button>
      </div>

      {total === 0 ? (
        <div className="flex items-center gap-2">
          <CheckCircle2 size={18} className="text-auth-ok shrink-0" aria-hidden />
          <p className="text-auth-texto text-sm font-medium">
            Nada a corrigir. O contrato está coerente com as regras conferidas.
          </p>
        </div>
      ) : (
        <div className="flex flex-wrap gap-4 text-sm">
          {impeditivos > 0 && (
            <p className="text-auth-erro font-medium">
              {impeditivos} {impeditivos === 1 ? 'impeditivo' : 'impeditivos'}
            </p>
          )}
          {exigencias > 0 && (
            <p className="text-auth-alerta font-medium">
              {exigencias} {exigencias === 1 ? 'exigência' : 'exigências'}
            </p>
          )}
          {avisos > 0 && (
            <p className="text-auth-texto-secundario font-medium">
              {avisos} {avisos === 1 ? 'aviso' : 'avisos'}
            </p>
          )}
        </div>
      )}

      {ordenados.length > 0 && (
        <ul className="flex flex-col gap-2.5">
          {ordenados.map((achado, indice) => (
            <li
              key={`${achado.regra}-${indice}`}
              className={`border-l-2 pl-3 ${COR[achado.severidade].split(' ')[0]}`}
            >
              <span
                title={EXPLICACAO[achado.severidade]}
                className={`rounded-auth-controle inline-block px-2 py-0.5 text-xs font-medium ${COR[achado.severidade]}`}
              >
                {ROTULO[achado.severidade]}
              </span>
              <p className="text-auth-texto mt-1.5 text-sm leading-relaxed">
                {achado.mensagem}
              </p>
              {achado.fundamento && (
                <p className="text-auth-texto-terciario mt-0.5 text-xs">
                  {achado.fundamento}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
